import { Grid, makeStyles, Theme, Typography, Paper, Hidden } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { ComponentFlow } from './types';
import { SubscriptionStatus, SubscriptionLifecycle } from '../../../state/subscription';
import { Link } from 'react-router-dom';
import InternalErrorBoundary from '../../utility/InternalErrorBoundary';
import LoadWithSpinner from '../../ui/Loading/LoadWithSpinner';
import { Site } from 'src/models/site';
import ActiveAlarms from '../../ui/activeAlarms';
import { primaryGreen } from 'src/data/colors';
import { PrimaryButton, SecondaryButton } from '../../ui/Buttons';
import PowerFlowDiagram from './PowerFlowDiagram';
import Weather from './Weather';
import EquipmentList from './EquipmentList';
import { Equipment } from 'src/models';
import TimeDisplay from './TimeDisplay';
import { PathBuilder } from 'src/routing';
import identifiers from 'src/tests/identifiers';
import StatusIndicator from 'src/components/ui/statusIndicator';
import UserInfoCard from './UserInfoCard';
import { borderRadius } from 'src/data/borders';
import LastUpdated from './LastUpdated';

interface DashboardPageProps {
  site: Site;
  flows: ComponentFlow[];
  dataStatus: SubscriptionStatus;
  equipmentList: Equipment[];
  lastFetchTime?: Date;
  siteFetchTime?: Date;
}

const DEFAULT_ERROR_MESSAGE =
  'Problem with dashboard configuration, please contact site administrator.';

const DashboardPage = (props: DashboardPageProps) => {
  const { site, flows, dataStatus, equipmentList, lastFetchTime, siteFetchTime } = props;
  const classes = useStyles();
  const hideForLaunch = true;

  return (
    <div className={classes.root}>
      <InternalErrorBoundary overrideMessage={DEFAULT_ERROR_MESSAGE}>
        {dataStatus !== SubscriptionLifecycle.ACTIVE ? (
          <div className={classes.contentWrapper}>
            <LoadWithSpinner message={'Loading dashboard...'} />
          </div>
        ) : (
          <Grid container data-testid={identifiers.dashboardPage.page}>
            <Grid container item alignItems="center" className={classes.mainTitleContainer}>
              <Grid item xs={12} sm={4}>
                <Typography variant="h3" className={classes.title}>
                  {site.name}
                </Typography>
                <Typography variant="body2" className={classes.subtitle}>
                  {site.address}
                </Typography>
              </Grid>
              <Grid
                container
                item
                xs={12}
                sm={8}
                direction="row"
                style={{ gap: '16px', width: '100%' }}
                justifyContent="flex-end"
                className={classes.papersContainer}
              >
                <Grid item xs={12} sm={4} md={5}>
                  <UserInfoCard site={site} />
                </Grid>

                {!hideForLaunch && (
                  <Grid item>
                    <Paper
                      variant="outlined"
                      className={classes.paper}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Weather />
                    </Paper>
                  </Grid>
                )}
                <Hidden xsDown>
                  <Grid item>
                    <Paper
                      variant="outlined"
                      className={classes.paper}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'fit-content',
                      }}
                    >
                      <Grid
                        container
                        item
                        alignItems="center"
                        justifyContent="space-between"
                        style={{
                          width: 'fit-content',
                        }}
                      >
                        <div>
                          <ActiveAlarms siteId={site.id} />
                        </div>
                        <div style={{ marginLeft: 16 }}>
                          {/* <Link // TODO: get tag from the backend 
                              to={PathBuilder.ANALYSIS_QUERY(site.id, [
                                'FG2303GRBF0426.FN1.SS7011_dev4032051239220018.F_ConnSt',
                              ])}
                            > */}
                          <StatusIndicator isOnline={site.isOnline} siteFetchTime={siteFetchTime} />
                          {/* </Link> */}
                        </div>
                      </Grid>
                    </Paper>
                  </Grid>
                </Hidden>
              </Grid>
            </Grid>
            <Grid container item alignItems="center" className={classes.powerflowHeader}>
              <Grid item xs={6}>
                <Typography variant="h3" className={classes.title2}>
                  Power Flow
                </Typography>
                <LastUpdated lastFetchTime={lastFetchTime} />
              </Grid>
              <Grid container item xs={6} justifyContent="flex-end">
                <PrimaryButton
                  className={classes.button}
                  component={Link}
                  to={PathBuilder.OPERATIONAL_SUMMARY(site.id)}
                >
                  See operations summary
                </PrimaryButton>
              </Grid>
            </Grid>
            <Grid container item>
              <Paper variant="outlined" className={classes.paperPowerflow}>
                <Hidden xsDown>
                  <Grid container item className={classes.powerFlowHeader}>
                    <Grid container item xs={12} md={6}>
                      <TimeDisplay site={site} />
                    </Grid>
                    {!hideForLaunch && (
                      <Grid container item xs={12} md={6} justifyContent="flex-end">
                        <SecondaryButton>Set a different time</SecondaryButton>
                      </Grid>
                    )}
                  </Grid>
                </Hidden>
                <Grid className={classes.powerflowWrap} item xs={12}>
                  <PowerFlowDiagram site={site} flows={flows} />
                </Grid>
                <Hidden smUp>
                  <Grid container item className={classes.powerFlowHeader}>
                    <Grid container item xs={12} md={6}>
                      <TimeDisplay site={site} />
                    </Grid>
                    {!hideForLaunch && (
                      <Grid container item xs={12} md={6} justifyContent="flex-end">
                        <SecondaryButton>Set a different time</SecondaryButton>
                      </Grid>
                    )}
                  </Grid>
                </Hidden>
              </Paper>
            </Grid>
            <Hidden smUp>
              <Grid item style={{ marginTop: 16 }}>
                <Paper
                  variant="outlined"
                  className={classes.paper}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'fit-content',
                  }}
                >
                  <Grid
                    container
                    item
                    alignItems="center"
                    justifyContent="space-between"
                    style={{
                      width: 'fit-content',
                    }}
                  >
                    <div>
                      <ActiveAlarms siteId={site.id} />
                    </div>
                    <div style={{ marginLeft: 16 }}>
                      <FiberManualRecordIcon htmlColor={primaryGreen} />
                    </div>
                  </Grid>
                </Paper>
              </Grid>
            </Hidden>
            <EquipmentList equipmentList={equipmentList} siteId={site.id} />
          </Grid>
        )}
      </InternalErrorBoundary>
    </div>
  );
};

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    padding: '24px',
    [theme.breakpoints.down('md')]: {
      padding: '16px',
    },
  },
  pageBackground: theme.mixins.pageBackground,
  powerflowSection: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #ccc',
    paddingBottom: '48px',
    zIndex: 0,
    [theme.breakpoints.down('xs')]: {
      paddingBottom: 0,
    },
  },
  powerflowWrap: {
    background: '#ebeff1',
    backgroundSize: '20px 20px',
    backgroundPosition: '-19px -19px',
    padding: '0 48px',
    '@media (max-width: 712px)': {
      padding: '0 4px',
    },
  },
  papersContainer: {
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      marginTop: '16px',
    },
  },
  energySection: {
    paddingTop: '16px',
  },
  mainTitleContainer: {
    [theme.breakpoints.down('sm')]: {
      marginTop: '16px',
    },
  },

  title: {
    fontSize: '32px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '-0.5px',
  },
  title2: {
    fontSize: '26px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    [theme.breakpoints.down('sm')]: {
      fontSize: '22px',
    },
  },
  powerflowHeader: {
    marginTop: '54px',
    [theme.breakpoints.down('sm')]: {
      marginTop: '16px',
    },
  },
  subtitle: {
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.17px',
  },
  paper: {
    padding: '16px',
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: borderRadius.md,
  },
  paperPowerflow: {
    width: '100%',
    marginTop: 16,
    borderRadius: borderRadius.md,
  },
  powerFlowHeader: {
    padding: '16px',
    borderBottom: '1px solid #ccc',
  },
  overline: {
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  body1: {
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
  },
  semibold: {
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
  },
  temperature: {
    fontSize: 20,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
  },
  cloudCoverage: {
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '0.15px',
  },
  button: {
    [theme.breakpoints.down('sm')]: {
      padding: '4px 10px',
    },
  },
}));

export default DashboardPage;
